import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _delete, _get, _post, _put } from "../../../../api/apiClient";
import { HttpStatusCode } from "axios";
import { API_STATUS } from "../../sliceUtil";
import { EMAIL_CAMPAIGN_STATUS } from "../../../../Components/Bank/CampaignManagement/campaignConstant";
import { CustomError } from "../../../../api/CustomError";
import {
  getEmailTemplateById,
  updateEmailTemplate,
} from "./emailTemplatesSlice";
import {
  fetchConsumerList,
  fetchConsumerListWithEventTag,
} from "./consumerListSlice";
import { setLoaderFalse, setLoaderTrue } from "../../../../Redux/loaderSlice";
import { CustomSuccess } from "../../../../api/CustomSuccess";
import store from "../../../../app/store";
import { setEmailCampaignContext } from "../../../../Redux/campaignHistorySlice";

const initEmailCampaignsState = {
  emailCampaigns: null,
  draftEmailCampaigns: null,
  emailCampaignsSummary: null,
  campaignStatusSummary: null,
  isLoading: API_STATUS.STATUS_IDLE,
  emailCampaignisLoading: API_STATUS.STATUS_IDLE,
  totalEmailCampaigns: 0,
};

export const _orgClientId = () => {
  const state = store.getState();
  return state?.orgReducer?.selectedFI?.orgClientId;
};

export const _currentDashboard = () => {
  const state = store.getState();
  return state.dashboardReducer.currentDashboard;
};

export const concurrent_fetchEmailCampaigns_fetchDraftEmailCampaigns_fetchEmailCampaignsStatus =
  createAsyncThunk(
    "campaigns/concurrent_fetchEmailCampaigns_fetchDraftEmailCampaigns_fetchEmailCampaignsStatus",
    async ({ paginationObj, disableLoader = false, channel }, thunkAPI) => {
      let isToastErrorOnce = false;
      try {
        if (disableLoader) thunkAPI.dispatch(setLoaderTrue());
        const [
          _fetchEmailCampaigns,
          _fetchDraftEmailCampaigns,
          _fetchEmailCampaignsStatus,
        ] = await Promise.all([
          thunkAPI.dispatch(
            fetchEmailCampaigns({ paginationObj, disableToast: true, channel }),
          ),
          thunkAPI.dispatch(
            fetchDraftEmailCampaigns({ disableToast: true, channel }),
          ),
          thunkAPI.dispatch(
            fetchEmailCampaignsSummary({ disableToast: true, channel }),
          ),
        ]);

        if (_fetchEmailCampaigns?.payload?.statusCode !== HttpStatusCode.Ok) {
          throw CustomError(_fetchEmailCampaigns?.payload, {
            disableToast: isToastErrorOnce,
          });
        }

        if (
          _fetchDraftEmailCampaigns?.payload?.statusCode !== HttpStatusCode.Ok
        ) {
          throw CustomError(_fetchDraftEmailCampaigns?.payload, {
            disableToast: isToastErrorOnce,
          });
        }

        if (
          _fetchEmailCampaignsStatus?.payload?.statusCode !== HttpStatusCode.Ok
        ) {
          throw CustomError(_fetchEmailCampaignsStatus?.payload, {
            disableToast: isToastErrorOnce,
          });
        }
        thunkAPI.dispatch(setLoaderFalse());
      } catch (err) {
        isToastErrorOnce = true;
        thunkAPI.dispatch(setLoaderFalse());
        return thunkAPI.rejectWithValue(err);
      }
    },
  );

export const fetchEmailCampaigns = createAsyncThunk(
  "campaigns/fetchEmailCampaigns",
  async (
    { paginationObj, disableToast, disableLoader = false, channel },
    thunkAPI,
  ) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Fetched Camapigns successfully.",
        errorMessage: "Failed to fetch camapigns!",
        disableToast: disableToast,
      };

      const _channel = channel == null ? `?channel=${channel}&` : "?";
      const _status = paginationObj?.selectedFilter?.code
        ? `status=${paginationObj?.selectedFilter?.code}&`
        : "";

      const _pageNumber = paginationObj?.currentPage
        ? `pageNumber=${paginationObj?.currentPage}`
        : "";
      const _pageSize =
        paginationObj?.limit > 0 ? `&PageSize=${paginationObj?.limit}` : "";

      const _sortOrder = paginationObj?.sortObj?.sortOrder
        ? `&orderBy=${paginationObj?.sortObj?.sortOrder}`
        : "";
      const _sortField = paginationObj?.sortObj?.sortField
        ? `&sortBy=${paginationObj?.sortObj?.sortField}`
        : "";
      const _searchBy = paginationObj?.searchData
        ? `&searchBy=${paginationObj?.searchData}`
        : "";

      const isAutomation = paginationObj?.selectedFilter?.isAutomation;
      const _automation =
        isAutomation !== undefined && isAutomation !== null
          ? `&isAutomation=${isAutomation}`
          : "";

      const _dashboardType = `&producttype=${_currentDashboard()?.KEY}`;

      const response = await _get(
        `${_orgClientId()}/campaign${_channel}${_status}${_pageNumber}${_pageSize}${_sortOrder}${_sortField}${_searchBy}${_automation}${_dashboardType}`,
        {},
        { metadata: { apiName: "fetchEmailCampaigns" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const {
        data: emailCampaigns,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      thunkAPI.dispatch(setLoaderFalse());
      return {
        emailCampaigns,
        paginationObj,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const fetchCampaignsStatusSummary = createAsyncThunk(
  "campaigns/fetchCampaignsStatusSummary",
  async (
    { campaignId, paginationObj, disableToast, disableLoader = false, channel },
    thunkAPI,
  ) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Fetched Camapigns Status Summary successfully.",
        errorMessage: "Failed to fetch camapigns Status Summary!",
        disableToast: disableToast,
      };

      const _pageNumber = paginationObj?.currentPage
        ? `pageNumber=${paginationObj?.currentPage}`
        : "";
      const _pageSize =
        paginationObj?.limit > 0 ? `&PageSize=${paginationObj?.limit}` : "";

      const _dashboardType = `&producttype=${_currentDashboard()?.KEY}`;

      const response = await _get(
        `${_orgClientId()}/campaign/${campaignId}/summary?${_pageNumber}${_pageSize}${_dashboardType}`,
        {},
        { metadata: { apiName: "fetchCampaignsStatusSummary" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      const {
        data: campaignStatusSummary,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      thunkAPI.dispatch(setLoaderFalse());
      return {
        campaignStatusSummary,
        paginationObj,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const fetchDraftEmailCampaigns = createAsyncThunk(
  "campaigns/fetchDraftEmailCampaigns",
  async ({ disableToast, disableLoader = false, channel }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Fetched Camapigns successfully.",
        errorMessage: "Failed to fetch camapigns!",
        disableToast: disableToast,
      };
      const _channel = channel == null ? `?channel=${channel}&` : "?";
      const _status = `status=${EMAIL_CAMPAIGN_STATUS?.DRAFT_CAMPAIGN}`;

      const _pageNumber = `&pageNumber=1`;
      const _pageSize = `&PageSize=4`;
      const _sortOrder = `&orderBy=DESC`;
      const _sortField = `&sortBy=createdOn`;
      const _dashboardType = `&producttype=${_currentDashboard()?.KEY}`;

      const response = await _get(
        `${_orgClientId()}/campaign${_channel}${_status}${_pageNumber}${_pageSize}${_sortOrder}${_sortField}${_dashboardType}`,
        {},
        { metadata: { apiName: "fetchDraftEmailCampaigns" } },
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      const {
        data: draftEmailCampaigns,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });
      thunkAPI.dispatch(setLoaderFalse());
      return {
        draftEmailCampaigns,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const fetchEmailCampaignsSummary = createAsyncThunk(
  "campaigns/fetchEmailCampaignsSummary",
  async ({ disableToast, disableLoader = false, channel }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Fetched camapigns summary successfully.",
        errorMessage: "Failed to fetch camapigns summary!",
        disableToast: disableToast,
      };
      const _channel = channel ? `?channel=${channel}` : "?";
      const _dashboardType = `&producttype=${_currentDashboard()?.KEY}`;

      const response = await _get(
        `${_orgClientId()}/campaign/summary${_channel}${_dashboardType}`,
        {},
        {
          signal: thunkAPI.signal,
          metadata: { apiName: "fetchEmailCampaignsSummary" },
        },
      );
      thunkAPI.signal.throwIfAborted();
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      const {
        data: emailCampaignsSummary,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });

      thunkAPI.dispatch(setLoaderFalse());
      return {
        emailCampaignsSummary,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      if (disableLoader) thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const concurrent_getEmailCampaignById_fetchConsumerList =
  createAsyncThunk(
    "campaigns/concurrent_getEmailCampaignById_fetchConsumerList",
    async ({ campaignId, disableLoader = false }, thunkAPI) => {
      try {
        if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());

        const [_getEmailCampaignById, _fetchConsumerList] = await Promise.all([
          thunkAPI.dispatch(
            getEmailCampaignById({ campaignId, disableLoader: true }),
          ),
          thunkAPI.dispatch(
            fetchConsumerList({
              paginationObj: {},
              disableLoader: false,
              isConsumerList: true,
            }),
          ),
        ]);

        if (_getEmailCampaignById?.payload?.statusCode !== HttpStatusCode.Ok) {
          CustomError(_getEmailCampaignById?.value?.payload);
        }

        if (_fetchConsumerList?.payload?.statusCode !== HttpStatusCode.Ok) {
          CustomError(_fetchConsumerList?.value?.payload);
        }

        thunkAPI.dispatch(setLoaderFalse());
        return {
          campaignResponse: _getEmailCampaignById?.payload,
          customerlistRes: _fetchConsumerList?.payload,
          statusCode: 200,
        };
      } catch (err) {
        thunkAPI.dispatch(setLoaderFalse());
        return thunkAPI.rejectWithValue(err);
      }
    },
  );

export const cascaded_getEmailCampaignById_getEmailTemplateById_fetchConsumerList =
  createAsyncThunk(
    "campaigns/cascaded_getEmailCampaignById_getEmailTemplateById_fetchConsumerList",
    async ({ campaignId, disableLoader = false }, thunkAPI) => {
      try {
        if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());

        const campaignResponse = await thunkAPI.dispatch(
          getEmailCampaignById({ campaignId, disableLoader: true }),
        );

        if (campaignResponse?.payload?.statusCode !== HttpStatusCode.Ok) {
          CustomError(campaignResponse, {
            errorMessage: "Failed to load campaign.",
            disableToast: true,
          });
        }

        const [_fetchEmailTemplates, _fetchConsumerList, _fetchEventTags] =
          await Promise.all([
            thunkAPI.dispatch(
              getEmailTemplateById({
                templateId:
                  campaignResponse?.payload?.campaignResponse?.templateId,
                disableLoader: false,
              }),
            ),
            thunkAPI.dispatch(
              fetchConsumerList({
                paginationObj: {},
                disableLoader: false,
                isConsumerList: true,
              }),
            ),
            thunkAPI.dispatch(
              fetchConsumerListWithEventTag({
                paginationObj: {},
                disableLoader: false,
                isConsumerList: true,
              }),
            ),
          ]);

        if (_fetchEmailTemplates?.payload?.statusCode !== HttpStatusCode.Ok) {
          CustomError(_fetchEmailTemplates?.value?.payload);
        }

        if (_fetchConsumerList?.payload?.statusCode !== HttpStatusCode.Ok) {
          CustomError(_fetchConsumerList?.value?.payload);
        }

        if (_fetchEventTags?.payload?.statusCode !== HttpStatusCode.Ok) {
          CustomError(_fetchEventTags?.value?.payload);
        }

        thunkAPI.dispatch(setLoaderFalse());
        return {
          campaignResponse: campaignResponse?.payload,
          templateResponse: _fetchEmailTemplates?.payload,
          customerlistRes: _fetchConsumerList?.payload,
          eventTagsResponse: _fetchEventTags?.payload,
          statusCode: 200,
        };
      } catch (err) {
        thunkAPI.dispatch(setLoaderFalse());
        return thunkAPI.rejectWithValue(err);
      }
    },
  );

export const getEmailCampaignById = createAsyncThunk(
  "campaigns/getEmailCampaignById",
  async ({ campaignId, disableLoader = false }, thunkAPI) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Get campaign successfully.",
        errorMessage: "Failed to get camapign!",
      };
      const response = await _get(
        `${_orgClientId()}/campaign/${campaignId}`,
        {},
        { metadata: { apiName: "getEmailCampaignById" } },
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      const {
        data: campaignResponse,
        error,
        message,
        status,
        statusCode,
        responseType,
      } = CustomSuccess(response, {
        ...config,
        disableToast: true,
      });
      thunkAPI.dispatch(setLoaderFalse());

      return {
        campaignResponse,
        error,
        message,
        status,
        statusCode,
        responseType,
      };
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const createEmailCampaign = createAsyncThunk(
  "campaigns/createEmailCampaign",
  async (
    { campaignObj, isSMSCampaign, smsCampaign, disableLoader = false },
    thunkAPI,
  ) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Campaign created successfully.",
        errorMessage: "Failed to create campaign!",
      };
      const _dashboardType = { producttype: _currentDashboard()?.KEY };
      const response = await _post(
        `${_orgClientId()}/campaign`,
        {
          ...campaignObj,
          ..._dashboardType,
        },
        { metadata: { apiName: "createEmailCampaign" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      if (isSMSCampaign) {
        await thunkAPI.dispatch(
          updateEmailTemplate({
            templateObj: smsCampaign,
            templateId: response?.data?.data?.templateId,
            disableToast: true,
          }),
        );
      }

      const _response = CustomSuccess(response, {
        ...config,
      });

      thunkAPI.dispatch(
        setEmailCampaignContext({
          ..._response?.data,
          messageContext: smsCampaign,
          selectedTemplateId: campaignObj?.templateId,
        }),
      );
      thunkAPI.dispatch(setLoaderFalse());
      return _response;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const updateEmailCampaign = createAsyncThunk(
  "campaigns/updateEmailCampaign",
  async ({ campaignObj, smsCampaign }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: campaignObj.isScheduleCampaign
          ? "Campaign scheduled successfully."
          : "Campaign updated successfully.",
        errorMessage: campaignObj.isScheduleCampaign
          ? "Failed to scheduled campaign!"
          : "Failed to update campaign!",
      };
      const _dashboardType = { producttype: _currentDashboard()?.KEY };
      const response = await _put(
        `${_orgClientId()}/campaign/${campaignObj?.uuid}`,
        { ...campaignObj, ..._dashboardType },
        { metadata: { apiName: "updateEmailCampaign" } },
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      const _response = CustomSuccess(response, {
        ...config,
      });
      thunkAPI.dispatch(
        setEmailCampaignContext({
          ..._response?.data,
          messageContext: smsCampaign,
          selectedTemplateId: campaignObj?.templateId,
        }),
      );
      thunkAPI.dispatch(setLoaderFalse());

      return _response;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const updateCreateEmailCampaign = createAsyncThunk(
  "campaigns/updateEmailCampaign",
  async ({ campaignObj, smsCampaign }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: campaignObj.isScheduleCampaign
          ? "Campaign scheduled successfully."
          : "Campaign updated successfully.",
        errorMessage: campaignObj.isScheduleCampaign
          ? "Failed to scheduled campaign!"
          : "Failed to update campaign!",
      };
      const _dashboardType = { producttype: _currentDashboard()?.KEY };
      const response = await _put(
        `${_orgClientId()}/campaign/${campaignObj?.uuid}/modify`,
        { ...campaignObj, ..._dashboardType },
        { metadata: { apiName: "updateCreateEmailCampaign" } },
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      const _response = CustomSuccess(response, {
        ...config,
      });
      thunkAPI.dispatch(
        setEmailCampaignContext({
          ..._response?.data,
          messageContext: smsCampaign,
          selectedTemplateId: campaignObj?.templateId,
        }),
      );
      thunkAPI.dispatch(setLoaderFalse());

      return _response;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const deleteEmailCampaign = createAsyncThunk(
  "campaigns/deleteEmailCampaign",
  async (
    { selectedCampaign, paginationObj, disableLoader = false, channel },
    thunkAPI,
  ) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Campaign deleted successfully.",
        errorMessage: "Failed to delete campaign!",
      };
      const response = await _delete(
        `${_orgClientId()}/campaign/${selectedCampaign?.uuid}`,
        { metadata: { apiName: "deleteEmailCampaign" } },
      );
      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }
      if (selectedCampaign?.status === EMAIL_CAMPAIGN_STATUS?.DRAFT_CAMPAIGN) {
        await thunkAPI.dispatch(
          fetchDraftEmailCampaigns({ disableToast: false, channel }),
        );
      }
      await thunkAPI.dispatch(
        fetchEmailCampaignsSummary({ disableToast: false, channel }),
      );
      await thunkAPI.dispatch(fetchEmailCampaigns({ paginationObj, channel }));
      const _response = CustomSuccess(response, {
        ...config,
      });
      thunkAPI.dispatch(setLoaderFalse());
      return _response;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const cancelEmailCampaign = createAsyncThunk(
  "campaigns/cancelEmailCampaign",
  async (
    { selectedCampaign, paginationObj, disableLoader = false, channel },
    thunkAPI,
  ) => {
    try {
      if (!disableLoader) thunkAPI.dispatch(setLoaderTrue());
      const config = {
        successMessage: "Campaign Cancelled successfully.",
        errorMessage: "Failed to cancel campaign!",
      };

      const response = await _delete(
        `${_orgClientId()}/campaign/${selectedCampaign?.uuid}/cancel`,
        { metadata: { apiName: "cancelEmailCampaign" } },
      );

      if (response?.statusCode !== HttpStatusCode.Ok) {
        throw CustomError(response, config);
      }

      if (selectedCampaign?.status === EMAIL_CAMPAIGN_STATUS?.DRAFT_CAMPAIGN) {
        await thunkAPI.dispatch(
          fetchDraftEmailCampaigns({ disableToast: false, channel }),
        );
      }
      paginationObj &&
        (await thunkAPI.dispatch(
          fetchEmailCampaignsSummary({ disableToast: false }),
        ));
      paginationObj &&
        (await thunkAPI.dispatch(
          fetchEmailCampaigns({ paginationObj, channel }),
        ));
      const _response = CustomSuccess(response, {
        ...config,
      });
      thunkAPI.dispatch(setLoaderFalse());
      return _response;
    } catch (err) {
      thunkAPI.dispatch(setLoaderFalse());
      return thunkAPI.rejectWithValue(err);
    }
  },
);

export const emailCampaignsSlice = createSlice({
  name: "emailCampaigns",
  initialState: initEmailCampaignsState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmailCampaigns.pending, (_state) => ({
        ..._state,
        emailCampaignisLoading: API_STATUS.STATUS_LOADING,
      }))
      .addCase(fetchEmailCampaigns.fulfilled, (_state, action) => {
        const { currentPage, limit } = action?.payload?.paginationObj || {};

        let offset = (currentPage - 1) * limit;
        const emailCampaignData =
          action?.payload?.emailCampaigns?.campaigns?.map((item, index) => {
            return { ...item, srNo: offset + index + 1 };
          });
        return {
          ..._state,
          emailCampaignisLoading: API_STATUS.STATUS_SUCCESS,
          emailCampaigns: {
            campaigns: emailCampaignData,
            // campaigns: action.payload.emailCampaigns.campaigns,
            totalCount: action.payload.emailCampaigns.totalCount,
          },
        };
      })
      .addCase(fetchEmailCampaigns.rejected, (_state, action) => ({
        emailCampaignisLoading: API_STATUS.STATUS_FAILURE,
        message: action.error.message || "Error while fetching email campaigns",
      }))
      .addCase(fetchDraftEmailCampaigns.pending, (_state) => ({
        ..._state,
        isLoading: API_STATUS.STATUS_LOADING,
      }))
      .addCase(fetchDraftEmailCampaigns.fulfilled, (_state, action) => {
        return {
          ..._state,
          isLoading: API_STATUS.STATUS_SUCCESS,
          draftEmailCampaigns: {
            campaigns: action.payload.draftEmailCampaigns.campaigns,
            totalCount: action.payload.draftEmailCampaigns.totalCount,
          },
        };
      })
      .addCase(fetchDraftEmailCampaigns.rejected, (_state, action) => ({
        isLoading: API_STATUS.STATUS_FAILURE,
        message:
          action.error.message || "Error while fetching draft email campaigns",
      }))
      .addCase(fetchEmailCampaignsSummary.pending, (_state) => ({
        ..._state,
        isLoading: API_STATUS.STATUS_LOADING,
      }))
      .addCase(fetchEmailCampaignsSummary.fulfilled, (_state, action) => {
        return {
          ..._state,
          isLoading: API_STATUS.STATUS_SUCCESS,
          emailCampaignsSummary: action.payload.emailCampaignsSummary,
        };
      })
      .addCase(fetchEmailCampaignsSummary.rejected, (_state, action) => ({
        isLoading: API_STATUS.STATUS_FAILURE,
        message:
          action.error.message ||
          "Error while fetching email campaigns summary",
      }));
  },
});

export const selectEmailCampaigns = (_state) => _state?.emailCampaigns;
